import { Component } from '@theme/component';
import { CartAddEvent, CartErrorEvent } from '@theme/events';

/**
 * AWP Product Form — adds the selected variant to the NATIVE cart drawer.
 *
 * Subscriptions are owned by the Moxie widget (app: subscription-custom). When a
 * customer picks "Subscription", Moxie's subscription-widget.js injects hidden
 * `properties[...]` inputs into THIS form (Tipo de compra, Frecuencia de entrega,
 * etc.) and also triggers this form's submit. Our only job is to submit the
 * form's own fields — so Moxie's properties ride along — as a urlencoded POST to
 * /cart/add.js (which always returns JSON and lets Moxie's Shopify Function apply
 * the discount), then morph + open the native cart drawer.
 *
 * Important: do NOT hand-build the payload or attach properties ourselves. Moxie
 * is the single source of truth for subscription state. We just need a clean
 * native add that returns JSON.
 */

class AwpProductForm extends Component {
  #productData = null;

  connectedCallback() {
    super.connectedCallback();

    const dataScript = this.querySelector('[ref="productData"]');
    if (dataScript) {
      try {
        this.#productData = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('AWP Product Form: Failed to parse product data');
      }
    }

    this.#bindSiblingEvents();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#unbindSiblingEvents();
  }

  #onVariantChange = (event) => {
    if (String(event.detail.productId) !== String(this.dataset.productId)) return;

    if (this.refs.variantInput) {
      this.refs.variantInput.value = event.detail.variantId;
    }

    if (this.refs.addToCartBtn) {
      if (!event.detail.available) {
        this.refs.addToCartBtn.disabled = true;
        this.refs.addToCartBtn.textContent = this.refs.addToCartBtn.dataset.soldText || 'Sold Out';
      } else {
        this.refs.addToCartBtn.disabled = false;
        this.refs.addToCartBtn.textContent = this.refs.addToCartBtn.dataset.addText || 'Add To Cart';
      }
    }
  };

  #bindSiblingEvents() {
    document.addEventListener('awp:variant-change', this.#onVariantChange);
  }

  #unbindSiblingEvents() {
    document.removeEventListener('awp:variant-change', this.#onVariantChange);
  }

  #currentQuantity() {
    const qtyInput = document.querySelector('.awp_quantity-selector__value');
    const qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    return qty && qty > 0 ? qty : 1;
  }

  /** Cart-drawer section ids so /cart/add.js returns rendered sections for morphing. */
  #cartSectionIds() {
    const ids = [];
    document.querySelectorAll('cart-items-component[data-section-id]').forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId && ids.indexOf(el.dataset.sectionId) === -1) {
        ids.push(el.dataset.sectionId);
      }
    });
    return ids;
  }

  /** @param {Event} event */
  handleSubmit(event) {
    event.preventDefault();

    const form = this.querySelector('form');
    if (!form) return;

    const btn = this.refs.addToCartBtn;
    if (btn) {
      btn.disabled = true;
      btn.classList.add('awp_add-to-cart__btn--loading');
      btn.textContent = btn.dataset.addingText || 'Añadiendo...';
    }

    // Build the body from the form itself so Moxie's injected hidden
    // properties[...] inputs are included. Must be urlencoded (NOT multipart /
    // JSON) for the Moxie Shopify Function to fire reliably.
    const params = new URLSearchParams();
    const formData = new FormData(form);
    for (const [key, value] of formData.entries()) {
      if (typeof value === 'string') params.append(key, value);
    }

    // The quantity stepper lives outside the form, so add it explicitly.
    if (!params.has('quantity')) {
      params.append('quantity', String(this.#currentQuantity()));
    }

    const sectionIds = this.#cartSectionIds();
    if (sectionIds.length > 0) {
      params.append('sections', sectionIds.join(','));
    }

    // /cart/add.js (not /cart/add) so the response is always JSON. Moxie's fetch
    // interceptor matches the /cart/add substring, so it still injects too.
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        Accept: '*/*',
      },
      body: params.toString(),
    })
      .then((response) => response.json())
      .then((response) => {
        // A failed /cart/add.js returns { status, message, description, errors }.
        const didError = !!response.status;

        if (didError) {
          this.dispatchEvent(
            new CartErrorEvent(this.id || '', response.message, response.description, response.errors)
          );
          this.#resetButton(false);
          if (this.refs.liveRegion) {
            this.refs.liveRegion.textContent = response.description || 'Error al añadir al carrito';
          }
          return;
        }

        if (this.refs.liveRegion) {
          this.refs.liveRegion.textContent = 'Producto añadido al carrito';
        }
        this.#resetButton(true);

        fetch('/cart.js')
          .then((r) => r.json())
          .then((cart) => {
            // CartAddEvent fires 'cart:update' → cart-items-component morphs the
            // drawer from response.sections (falling back to a server re-render if
            // sections are absent), and the cart drawer auto-opens.
            this.dispatchEvent(
              new CartAddEvent({}, this.id || '', {
                didError: false,
                source: 'awp-product-form',
                itemCount: cart.item_count,
                productId: this.dataset.productId,
                sections: response.sections,
              })
            );
          });

        // Explicitly open the drawer too, in case auto-open is disabled.
        const drawer =
          document.querySelector('cart-drawer-component') || document.querySelector('cart-drawer');
        if (drawer && typeof drawer.open === 'function') drawer.open();
      })
      .catch((error) => {
        console.error('AWP Product Form: Cart add error', error);
        this.#resetButton(false);
      });
  }

  #resetButton(success) {
    const btn = this.refs.addToCartBtn;
    if (!btn) return;

    setTimeout(
      () => {
        btn.disabled = false;
        btn.classList.remove('awp_add-to-cart__btn--loading');
        btn.textContent = btn.dataset.addText || 'Add To Cart';

        if (!this.#productData || !this.#productData.available) {
          btn.disabled = true;
          btn.textContent = btn.dataset.soldText || 'Sold Out';
        }
      },
      success ? 1000 : 500
    );
  }
}

if (!customElements.get('awp-product-form')) {
  customElements.define('awp-product-form', AwpProductForm);
}
